'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useEmailTemplatesList } from '@/lib/api/emailTemplates'
import { EmailTemplatesTable } from './components/EmailTemplatesTable'
import { MainTableState } from '@/lib/utils/table-state-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { Link } from 'react-router-dom'

/**
 * Store settings page - Email Templates section
 * Displays email templates for a specific store
 */
function EmailTemplatesPageInner() {
    const { t } = useTranslation(['emails', 'common'])
    const { id, storeRoutes } = useStoreNavigate()

    // Table state managed by EmailTemplatesTable
    const [tableState, setTableState] = React.useState<MainTableState>({
        pagination: { pageIndex: 0, pageSize: 20 },
        sorting: [{ id: 'createdAt', desc: true }],
        columnFilters: [],
    })

    const {
        data: templatesData,
        isLoading: loading,
        error,
        refetch,
    } = useEmailTemplatesList(
        { storeId: id },
        {
            enabled: !!id,
        },
    )

    const templates = templatesData?.templates || []

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
                    <p className="text-muted-foreground text-sm">
                        {t('description')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={storeRoutes.EMAILS_SETTINGS()}>
                        <Button variant="outline" className="w-full sm:w-auto">
                            {t('domainSettingsLink')}
                        </Button>
                    </Link>
                    <Link to={storeRoutes.EMAILS_CREATE()}>
                        <Button className="w-full sm:w-auto" >
                            <Plus className="mr-2 h-4 w-4" />
                            {t('addTemplate')}
                        </Button>
                    </Link>
                </div>
            </div>

            <EmailTemplatesTable
                templates={templates}
                loading={loading}
                onTableStateChange={setTableState}
                onRefetch={refetch}
            />

            {error && (
                <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error instanceof Error ? error.message : t('loadError')}
                </div>
            )}
        </div>
    )
}

export const EmailTemplatesPage = withRequiredPermission(EmailTemplatesPageInner, PERMISSIONS.STORES_VIEW)
